import { motion, AnimatePresence } from 'framer-motion'
import { OpenImgContextProvider } from 'openimg/react'
import { useState } from 'react'
import {
	data,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useMatches,
	useLocation,
	type ShouldRevalidateFunctionArgs,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { type Route } from './+types/root.ts'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { Footer } from './components/footer.tsx'
import { InlineLogo, InlineLogoDarkBg } from './components/inline-logo.tsx'
import { LetterSwapForward } from './components/letter-swap.tsx'
import { CtaSamenwerken } from './components/marketing/cta-samenwerken.tsx'
import { PreLaunchPage } from './components/pre-launch-page.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { StealthPage } from './components/stealth-page.tsx'
import { useToast } from './components/toaster.tsx'
import { AnimatedHamburger } from './components/ui/animated-hamburger.tsx'
import { GooeySearchBar } from './components/ui/animated-search-bar.tsx'
import { href as iconsHref } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import { ViewTransitionLink } from './components/view-transition-link.tsx'
import { useOptionalTheme, useTheme } from './routes/resources/theme-switch.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { pipeHeaders } from './utils/headers.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl, getImgSrc } from './utils/misc.tsx'
import {
	NavigationThemeProvider,
	useNavigationTheme,
} from './utils/navigation-theme-context.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { safariDetectionScript } from './utils/safari-detection.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'

export const links: Route.LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ data }) => {
	// In stealth mode, return minimal meta tags - no title, no description
	if (data?.isStealthMode) {
		return []
	}
	return [
		{ title: data ? 'Ignite Identity' : 'Error | Ignite Identity' },
		{ name: 'description', content: 'Ignite Identity — Bureau met een social missie. Merkstrategie, merkverhaal, identiteit, interne en externe communicatie, campagne.' },
	]
}

export async function loader({ request }: Route.LoaderArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	// Check database settings for stealth mode, pre-launch mode, search bar, and footer contact
	const [
		stealthSetting,
		preLaunchSetting,
		searchBarSetting,
		footerContactPhoneSetting,
		footerContactEmailSetting,
		footerContactCompanySetting,
		footerContactPostalSetting,
		footerContactVisitSetting,
	] = await Promise.all([
		prisma.siteSetting.findUnique({ where: { key: 'stealthMode' } }),
		prisma.siteSetting.findUnique({ where: { key: 'preLaunchMode' } }),
		prisma.siteSetting.findUnique({ where: { key: 'searchBarEnabled' } }),
		prisma.siteSetting.findUnique({ where: { key: 'footerContactPhone' } }),
		prisma.siteSetting.findUnique({ where: { key: 'footerContactEmail' } }),
		prisma.siteSetting.findUnique({ where: { key: 'footerContactCompany' } }),
		prisma.siteSetting.findUnique({ where: { key: 'footerContactPostal' } }),
		prisma.siteSetting.findUnique({ where: { key: 'footerContactVisit' } }),
	])
	const isStealthMode = stealthSetting?.value === 'true'
	const isPreLaunchMode = preLaunchSetting?.value === 'true'
	// Search bar is enabled by default if no setting exists (null/undefined or empty string)
	const isSearchBarEnabled = !searchBarSetting?.value || searchBarSetting.value === 'true'

	const user = userId
		? await time(
				() =>
					prisma.user.findUnique({
						select: {
							id: true,
							name: true,
							username: true,
							image: { select: { objectKey: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = await honeypot.getInputProps()

	// Fetch footer sections and links
	const footerSections = await prisma.footerSection.findMany({
		orderBy: { order: 'asc' },
		include: {
			links: {
				orderBy: { order: 'asc' },
				include: { page: { select: { slug: true, pageType: true } } },
			},
		},
	})

	// Fetch menu items
	const menuItems = await prisma.menuItem.findMany({
		orderBy: { order: 'asc' },
		include: { page: { select: { slug: true, pageType: true } } },
	})

	// Fetch default CTA
	const defaultCta = await prisma.defaultCta.findFirst({
		where: { isActive: true },
	})

	// Check if user is admin - needed to determine if they can bypass pre-launch mode
	const isAdmin = user?.roles.some((role) => role.name === 'admin') ?? false

	// Determine if we should show stealth or pre-launch page
	// Stealth mode has highest priority and overrules pre-launch mode
	// Both modes allow: admin users to access full site, auth routes to work
	const url = new URL(request.url)
	const isAuthRoute =
		url.pathname.startsWith('/login') ||
		url.pathname.startsWith('/signup') ||
		url.pathname.startsWith('/forgot-password') ||
		url.pathname.startsWith('/verify') ||
		url.pathname.startsWith('/onboarding') ||
		url.pathname.startsWith('/auth') ||
		url.pathname.startsWith('/resources')

	// Stealth mode: highest priority - shows blank "domain registered" page
	const showStealthPage = isStealthMode && !isAdmin && !isAuthRoute
	// Pre-launch mode: shows email signup page (only if stealth is not active)
	const showPreLaunchPage = !showStealthPage && isPreLaunchMode && !isAdmin && !isAuthRoute

	const footerContactInfo = {
		phone: footerContactPhoneSetting?.value || null,
		email: footerContactEmailSetting?.value || null,
		company: footerContactCompanySetting?.value || null,
		postalAddress: footerContactPostalSetting?.value || null,
		visitAddress: footerContactVisitSetting?.value || null,
	}

	return data(
		{
			user,
			footerSections,
			footerContactInfo,
			menuItems,
			defaultCta,
			isStealthMode,
			isPreLaunchMode,
			isSearchBarEnabled,
			isAdmin,
			showStealthPage,
			showPreLaunchPage,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

// Prevent revalidation for page autosave operations from admin pages
// This stops the constant loader re-fetching during editing
export function shouldRevalidate({
	formData,
	defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs) {
	const intent = formData?.get('intent')
	// Skip revalidation for autosave operations
	if (intent === 'save-blocks' || intent === 'save' || intent === 'save-all') {
		return false
	}
	return defaultShouldRevalidate
}

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
	stealthMode = false,
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string | undefined>
	stealthMode?: boolean
}) {
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'

	// In stealth mode, render a completely minimal HTML document
	// No meta tags, no links (except essential stylesheet), no identifying info
	if (stealthMode) {
		return (
			<html lang="nl">
				<head>
					<meta charSet="utf-8" />
					<meta name="viewport" content="width=device-width,initial-scale=1" />
					<meta name="robots" content="noindex, nofollow" />
					<link rel="stylesheet" href={tailwindStyleSheetUrl} />
				</head>
				<body className="bg-white">
					{children}
					<Scripts nonce={nonce} />
				</body>
			</html>
		)
	}

	return (
		<html lang="nl" className={`${theme} h-full overflow-x-hidden`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{ __html: safariDetectionScript }}
				/>
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	const theme = useOptionalTheme()
	// Only show stealth page (minimal HTML) for non-admin visitors
	// Admins should get the full HTML document even in stealth mode
	const showStealthDocument = data?.showStealthPage ?? false
	return (
		<Document
			nonce={nonce}
			theme={theme}
			env={data?.ENV}
			stealthMode={showStealthDocument}
		>
			{children}
		</Document>
	)
}

// Default fallback nav links (used if no menu items in database)
const defaultNavLinks = [
	{ to: '/ignite', label: 'Ignite' },
	{ to: '/cases', label: 'Werk' },
	{ to: '/werkwijze', label: 'Werkwijze' },
	{ to: '/nieuws', label: 'Nieuws' },
	{ to: '/kennisdelen', label: 'Kennisdelen' },
	{ to: '/catch-up', label: 'Catch up' },
]

// Animation variants for mobile menu
const easeOutCubic: [number, number, number, number] = [0.215, 0.61, 0.355, 1]
const easeMaterial: [number, number, number, number] = [0.4, 0, 0.2, 1]

const menuVariants = {
	closed: {
		opacity: 0,
		transition: {
			duration: 0.3,
			ease: easeMaterial,
		},
	},
	open: {
		opacity: 1,
		transition: {
			duration: 0.4,
			ease: easeMaterial,
		},
	},
}

const menuItemVariants = {
	closed: {
		opacity: 0,
		y: 40,
	},
	open: (i: number) => ({
		opacity: 1,
		y: 0,
		transition: {
			duration: 0.5,
			delay: 0.1 + i * 0.08,
			ease: easeOutCubic,
		},
	}),
}

const ctaVariants = {
	closed: {
		opacity: 0,
		y: 20,
		scale: 0.9,
	},
	open: {
		opacity: 1,
		y: 0,
		scale: 1,
		transition: {
			duration: 0.5,
			delay: 0.6,
			ease: easeOutCubic,
		},
	},
}

function App() {
	const data = useLoaderData<typeof loader>()
	const theme = useTheme()
	const { backgroundColor, isScrolled, isDarkBackground } = useNavigationTheme()
	const matches = useMatches()
	const location = useLocation()
	const isOnSearchPage = matches.find((m) => m.id === 'routes/users/index')
	const searchBar = isOnSearchPage || !data.isSearchBarEnabled ? null : <GooeySearchBar />
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
	useToast(data.toast)

	// Use dynamic menu items if available, otherwise fallback to defaults
	const navLinks =
		data.menuItems && data.menuItems.length > 0
			? data.menuItems.map((item) => ({
					to: item.page?.slug
						? item.page.pageType === 'service'
							? `/diensten/${item.page.slug.replace(/^diensten-/, '')}`
							: `/${item.page.slug}`
						: item.url || '#',
					label: item.label,
					isExternal: item.isExternal,
				}))
			: defaultNavLinks

	// Hide nav, footer, and CTA for admin routes
	const isAdminRoute = location.pathname.startsWith('/admin')

	// Show admin bar when admin is logged in and site is in stealth/pre-launch mode
	const showAdminBar =
		data.isAdmin && (data.isStealthMode || data.isPreLaunchMode)

	// Show stealth page when enabled (highest priority - for non-admin visitors)
	if (data.showStealthPage) {
		return <StealthPage />
	}

	// Show pre-launch page when enabled (for non-admin visitors)
	if (data.showPreLaunchPage) {
		return (
			<>
				<PreLaunchPage />
				<EpicToaster closeButton position="top-center" theme={theme} />
			</>
		)
	}

	// Admin routes render without nav/footer (admin has its own layout)
	if (isAdminRoute) {
		return (
			<OpenImgContextProvider
				optimizerEndpoint="/resources/images"
				getSrc={getImgSrc}
			>
				<div className="flex min-h-screen flex-col">
					<Outlet />
				</div>
				<EpicToaster closeButton position="top-center" theme={theme} />
				<EpicProgress />
			</OpenImgContextProvider>
		)
	}

	// Stealth mode for auth routes: render without nav/footer/CTA, minimal styling
	// Only affects auth routes - admins browsing normal pages should see full layout
	const isAuthRoute =
		location.pathname.startsWith('/login') ||
		location.pathname.startsWith('/signup') ||
		location.pathname.startsWith('/forgot-password') ||
		location.pathname.startsWith('/verify') ||
		location.pathname.startsWith('/onboarding') ||
		location.pathname.startsWith('/auth')

	if (data.isStealthMode && isAuthRoute) {
		return (
			<div className="flex min-h-screen flex-col">
				<Outlet />
			</div>
		)
	}

	return (
		<OpenImgContextProvider
			optimizerEndpoint="/resources/images"
			getSrc={getImgSrc}
		>
			{showAdminBar && (
				<AdminBar
					isStealthMode={data.isStealthMode}
					isPreLaunchMode={data.isPreLaunchMode}
				/>
			)}
			<div className="flex min-h-screen flex-col">
				<header
					className={`fixed right-0 left-0 z-50 px-6 transition-all duration-300 ease-out md:px-12 lg:px-24 ${
						isScrolled ? 'backdrop-blur-md' : ''
					} ${showAdminBar ? 'top-10' : 'top-0'}`}
					style={{
						backgroundColor: isScrolled
							? `${backgroundColor}E6`
							: 'transparent',
					}}
				>
					<nav className="mx-auto flex w-full max-w-[1920px] items-center justify-between py-5">
						{/* Logo */}
						<Logo />

						{/* Desktop Navigation - Right aligned before search */}
						<div className="hidden flex-1 items-center justify-end gap-5 xl:flex">
							{navLinks.map((link) => {
								const isActive = location.pathname.startsWith(link.to)
								return (
									<ViewTransitionLink
										key={link.to}
										to={link.to}
										className={`text-lg font-medium transition-colors ${
											isActive
												? 'text-[#f6921e]'
												: isDarkBackground
													? 'text-white hover:text-white/80'
													: 'text-black hover:text-black/70'
										}`}
									>
										<LetterSwapForward
											label={link.label}
											reverse={true}
											staggerFrom="center"
											staggerDuration={0.01}
										/>
									</ViewTransitionLink>
								)
							})}

							{/* Search Bar - Desktop (inline with nav) */}
							<div className="max-w-xs">{searchBar}</div>
						</div>

						{/* Mobile Menu Button - Animated Hamburger (placeholder for layout) */}
						<div className="flex items-center gap-4 xl:hidden">
							<div style={{ width: 36, height: 36 }} />
						</div>
					</nav>
				</header>

				{/* Fixed Animated Hamburger - Always visible above everything */}
				<AnimatedHamburger
					isOpen={mobileMenuOpen}
					onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
					color={mobileMenuOpen ? '#ffffff' : (isDarkBackground ? '#ffffff' : '#1a1a1a')}
					size={36}
					className={`fixed right-6 z-[70] xl:hidden ${showAdminBar ? 'top-15' : 'top-5'}`}
				/>

				{/* Full-screen Mobile Menu */}
				<AnimatePresence>
					{mobileMenuOpen && (
						<motion.div
							className="fixed inset-0 z-[60] flex flex-col bg-[#231f20] xl:hidden"
							variants={menuVariants}
							initial="closed"
							animate="open"
							exit="closed"
						>
							{/* Mobile Menu Header */}
							<div className="container flex items-center justify-between py-5">
								{/* Logo for dark background */}
								<ViewTransitionLink
									to="/"
									className="h-7"
									onClick={() => setMobileMenuOpen(false)}
								>
									<InlineLogoDarkBg className="h-7 w-auto" />
								</ViewTransitionLink>

								{/* Spacer for hamburger position (hamburger is fixed positioned) */}
								<div style={{ width: 36, height: 36 }} />
							</div>

							{/* Navigation Links */}
							<nav className="container flex flex-1 flex-col justify-center py-8">
								<ul className="flex flex-col gap-2">
									{navLinks.map((link, i) => {
										const isActive = location.pathname.startsWith(link.to)
										return (
											<motion.li
												key={link.to}
												custom={i}
												variants={menuItemVariants}
												initial="closed"
												animate="open"
												exit="closed"
											>
												<ViewTransitionLink
													to={link.to}
													onClick={() => setMobileMenuOpen(false)}
													className="group relative inline-block"
												>
													<span
														className={`text-[2.75rem] leading-tight font-bold tracking-tight transition-colors sm:text-6xl ${
															isActive
																? 'text-[#e91e8c]'
																: 'text-white hover:text-white/80'
														}`}
														style={{
															fontFamily:
																'"Helvetica Neue", "HelveticaNeue", Helvetica, Arial, sans-serif',
														}}
													>
														{link.label}
													</span>
													{/* Underline for active item */}
													{isActive && (
														<motion.div
															className="absolute -bottom-1 left-0 h-[2px] w-full bg-[#e91e8c]"
															layoutId="activeUnderline"
															transition={{
																duration: 0.3,
																ease: [0.215, 0.61, 0.355, 1],
															}}
														/>
													)}
												</ViewTransitionLink>
											</motion.li>
										)
									})}
								</ul>
							</nav>

							{/* Bottom CTA */}
							<motion.div
								className="container pb-12"
								variants={ctaVariants}
								initial="closed"
								animate="open"
								exit="closed"
							>
								<ViewTransitionLink
									to="/catch-up"
									onClick={() => setMobileMenuOpen(false)}
									className="inline-flex items-center justify-center rounded-full px-8 py-4 text-lg font-semibold text-white transition-transform hover:scale-105 active:scale-95"
									style={{
										background:
											'linear-gradient(135deg, #f6921e 0%, #e91e8c 100%)',
									}}
								>
									Samenwerken
								</ViewTransitionLink>
							</motion.div>
						</motion.div>
					)}
				</AnimatePresence>

				<div className="flex flex-col">
					<Outlet />
				</div>

				<div className="mt-auto">
					<ResolvedCta defaultCta={data.defaultCta} />
					<Footer sections={data.footerSections} contactInfo={data.footerContactInfo} />
				</div>
			</div>
			<EpicToaster closeButton position="top-center" theme={theme} />
			<EpicProgress />
		</OpenImgContextProvider>
	)
}

function AdminBar({
	isStealthMode,
	isPreLaunchMode,
}: {
	isStealthMode: boolean
	isPreLaunchMode: boolean
}) {
	const modeText = isStealthMode
		? 'Stealth mode actief'
		: isPreLaunchMode
			? 'Pre-launch mode actief'
			: null

	if (!modeText) return null

	return (
		<div className="fixed top-0 left-0 right-0 z-[100] h-10 bg-[#f6921e] text-white text-sm">
			<div className="container flex h-full items-center justify-between">
				<span className="font-medium">
					{modeText} — Je bent ingelogd als admin
				</span>
				<a
					href="/logout"
					className="underline hover:no-underline font-medium"
				>
					Uitloggen
				</a>
			</div>
		</div>
	)
}

interface CtaConfig {
	mode: string
	title?: string | null
	buttonText?: string | null
	buttonLink?: string | null
	imageUrl?: string | null
	imageAlt?: string | null
	highlightWords?: string | null
	fontSizePreset?: string | null
	showButton?: boolean | null
}

function usePageCtaConfig(): CtaConfig | null {
	const matches = useMatches()
	// Walk from deepest match to find the route with ctaConfig
	for (let i = matches.length - 1; i >= 0; i--) {
		const matchData = matches[i]?.data as Record<string, unknown> | undefined
		if (matchData && 'ctaConfig' in matchData && matchData.ctaConfig) {
			return matchData.ctaConfig as CtaConfig
		}
	}
	return null
}

function ResolvedCta({
	defaultCta,
}: {
	defaultCta: {
		title: string
		buttonText: string
		buttonLink: string
		imageUrl?: string | null
		imageAlt?: string | null
		highlightWords?: string | null
		fontSizePreset?: string | null
		showButton?: boolean | null
	} | null
}) {
	const pageCtaConfig = usePageCtaConfig()

	// If page says hidden, show nothing
	if (pageCtaConfig?.mode === 'hidden') {
		return null
	}

	// If page says custom, use page-specific values
	if (pageCtaConfig?.mode === 'custom') {
		return (
			<CtaSamenwerken
				title={pageCtaConfig.title}
				buttonText={pageCtaConfig.buttonText}
				buttonLink={pageCtaConfig.buttonLink}
				imageUrl={pageCtaConfig.imageUrl}
				imageAlt={pageCtaConfig.imageAlt}
				highlightWords={pageCtaConfig.highlightWords}
				fontSizePreset={pageCtaConfig.fontSizePreset}
				showButton={pageCtaConfig.showButton ?? true}
			/>
		)
	}

	// Default mode: use the global default CTA
	if (defaultCta) {
		return (
			<CtaSamenwerken
				title={defaultCta.title}
				buttonText={defaultCta.buttonText}
				buttonLink={defaultCta.buttonLink}
				imageUrl={defaultCta.imageUrl}
				imageAlt={defaultCta.imageAlt}
				highlightWords={defaultCta.highlightWords}
				fontSizePreset={defaultCta.fontSizePreset}
				showButton={defaultCta.showButton}
			/>
		)
	}

	// Fallback: render with defaults
	return <CtaSamenwerken />
}

function Logo() {
	const { isDarkBackground } = useNavigationTheme()

	return (
		<ViewTransitionLink to="/" className="group relative block h-7 md:h-10">
			{/* Use inline SVG logos to fix Safari gradient rendering bug */}
			{/* See: https://github.com/airbnb/visx/issues/317 */}
			{isDarkBackground ? (
				<InlineLogoDarkBg className="h-7 w-auto md:h-10" />
			) : (
				<InlineLogo className="h-7 w-auto md:h-10" />
			)}
		</ViewTransitionLink>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<NavigationThemeProvider>
				<App />
			</NavigationThemeProvider>
		</HoneypotProvider>
	)
}

export default AppWithProviders

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
